/**
 * Modello della bacheca dello Smistamento (T39, redesign v4).
 * Puro: nessuna rete, nessun `window`, nessun React. I componenti
 * `DispatchView`, `ChannelRail`, `DispatchBoard` e `DispatchCard` ricevono da
 * qui colonne, testi, paginazione, regole di trasferimento e drag & drop.
 *
 * Design: `Centro Ticket v4.dc.html` L74-L170 (markup) e L876-L918 (logica).
 * Correzioni al prototipo (inventario §2.4, v4-plan T39):
 * - paginazione per colonna (prime 20 + «Mostra altri») invece di tutto in pagina;
 * - ordinamento del server (CT-43: da leggere, poi ultima attivita), NON priorita+numero;
 * - drag & drop = apre il pannello rapido col canale preselezionato (Q3), mai trasferimento;
 * - trasferimento solo con `supporttickets.transfer` e sui canali permessi (`canRowTransfer`, T31).
 */
import { buildQueueRow, relativeAge, type QueueRowModel } from '../v3/queueModel';
import { slaBadge, type S3SlaBadge } from '../v3/visual';
import { classifySla } from '../../../utils/supportSla';
import type {
  BoardTicketListItem,
  DispatchBoardColumn,
  DispatchChannelSummary,
  DispatchInboundSummary,
  DispatchListParams,
  DispatchSummary,
  TicketListItem,
} from '../../../services/supportApi';

// ───────────────────────── Colonne ─────────────────────────

export interface DispatchColumnDef {
  key: DispatchBoardColumn;
  label: string;
  /** Sottotitolo mono (L126-L127). */
  sub: string;
  /** Testo della colonna vuota senza filtri (L884-L889). */
  empty: string;
  /** Colonna principale evidenziata (sfumatura verde, L892). */
  main: boolean;
  /** Bottone «Trasferisci» sulla card (solo da smistare / non presi, L156-L160). */
  transferButton: boolean;
  /** Estratto del primo messaggio (solo «Da smistare», L147, N8). */
  excerpt: boolean;
  /** «ATTESA 10 H» dal tempo di arrivo nel canale (solo «Non presi», L151, N9). */
  wait: boolean;
  /** Avatar degli assegnatari (solo lavorazione/attesa, L153). */
  avatars: boolean;
}

export const DISPATCH_COLUMNS: readonly DispatchColumnDef[] = [
  {
    key: 'smi', label: 'Da smistare', sub: 'NEL CANALE PREDEFINITO', empty: 'Nessun ticket da smistare.',
    main: true, transferButton: true, excerpt: true, wait: false, avatars: false,
  },
  {
    key: 'np', label: 'Non presi in carico', sub: 'NEL CANALE GIUSTO, SENZA ASSEGNATARIO',
    empty: 'Tutti i ticket sono presi in carico.', main: false, transferButton: true, excerpt: false, wait: true, avatars: false,
  },
  {
    key: 'lav', label: 'In lavorazione', sub: 'CHI LI SEGUE E SLA', empty: 'Nessun ticket in lavorazione.',
    main: false, transferButton: false, excerpt: false, wait: false, avatars: true,
  },
  {
    key: 'att', label: 'In attesa', sub: 'RISPOSTA DEL RICHIEDENTE', empty: 'Nessuna attesa.',
    main: false, transferButton: false, excerpt: false, wait: false, avatars: true,
  },
];

/** Sottotitolo della colonna: «Da smistare» nomina il canale predefinito (L126). */
export const columnSubtitle = (col: DispatchColumnDef, inboundName?: string | null): string =>
  col.key === 'smi' && inboundName ? `${col.sub} · ${inboundName.toUpperCase()}` : col.sub;

/** Filtri della bacheca: gli stessi per le quattro colonne. */
export interface DispatchFilters {
  search: string;
  /** Canale scelto nella colonna canali (`?canale=`), '' = tutti. */
  groupId: string;
  /** KPI «FUORI SLA» acceso. */
  slaBreach: boolean;
}

export const hasActiveFilters = (f: DispatchFilters): boolean =>
  Boolean(f.search.trim() || f.groupId || f.slaBreach);

/** Testo della colonna vuota: con filtri accesi non si dice «tutti presi in carico». */
export const columnEmptyText = (col: DispatchColumnDef, f: DispatchFilters): string =>
  hasActiveFilters(f) ? 'Nessun ticket trovato.' : col.empty;

// ───────────────────────── Paginazione per colonna ─────────────────────────

export const COLUMN_PAGE_SIZE = 20;
/** Tetto del backend (`listTickets`: limit ≤ 100). */
export const COLUMN_MAX_LIMIT = 100;

/** Parametri lista di una colonna (senza paginazione). */
export const columnListParams = (col: DispatchBoardColumn, f: DispatchFilters): DispatchListParams => {
  const p: DispatchListParams = { board: col };
  const search = f.search.trim();
  if (search) p.search = search;
  if (f.groupId) p.groupId = f.groupId;
  if (f.slaBreach) p.slaBreach = true;
  return p;
};

/** «Mostra altri»: pagina successiva da 20, calcolata su quanti ne sono gia caricati. */
export const nextPageRequest = (loaded: number): { page: number; limit: number } => ({
  page: Math.floor(Math.max(0, loaded) / COLUMN_PAGE_SIZE) + 1,
  limit: COLUMN_PAGE_SIZE,
});

/**
 * Ricarica (tempo reale, cambio filtri): una sola richiesta che copre quanto era
 * gia a schermo, arrotondato a pagine intere da 20 (min 20, max 100), cosi
 * «Mostra altri» dopo una ricarica continua dal punto giusto.
 */
export const refreshRequest = (loaded: number): { page: 1; limit: number } => {
  const pages = Math.max(1, Math.ceil(Math.max(0, loaded) / COLUMN_PAGE_SIZE));
  return { page: 1, limit: Math.min(COLUMN_MAX_LIMIT, pages * COLUMN_PAGE_SIZE) };
};

/** Accoda una pagina mantenendo l'ordine del server (CT-43) e senza doppioni. */
export const appendPage = <T extends { _id: string }>(prev: readonly T[], next: readonly T[]): T[] => {
  const seen = new Set(prev.map((t) => t._id));
  return [...prev, ...next.filter((t) => !seen.has(t._id))];
};

export const hasMore = (loaded: number, total: number): boolean => loaded < total;

export const moreLabel = (loaded: number, total: number): string => {
  const rest = Math.max(0, total - loaded);
  return `Mostra altri (${Math.min(COLUMN_PAGE_SIZE, rest)} di ${rest})`;
};

// ───────────────────────── Tempo reale (CT-44) ─────────────────────────

export const REALTIME_THROTTLE_MS = 1000;

/**
 * Throttle a 1 s con coda: 0 = ricarica subito; altrimenti fra quanti ms.
 * `lastAt` = ultima ricarica partita (0 = mai).
 */
export const throttleDelay = (lastAt: number, now: number, windowMs: number = REALTIME_THROTTLE_MS): number => {
  if (!lastAt) return 0;
  const elapsed = now - lastAt;
  return elapsed >= windowMs ? 0 : windowMs - elapsed;
};

// ───────────────────────── Canali ─────────────────────────

/** Canale per id o slug (`?canale=` accetta lo slug dei vecchi link e, in mancanza, l'id). */
export const resolveChannelParam = (
  param: string,
  summary: DispatchSummary | null
): { groupId: string; nome: string; slug?: string } | null => {
  if (!param || !summary) return null;
  const all: Array<DispatchChannelSummary | DispatchInboundSummary> = [
    ...(summary.inboundDefault ? [summary.inboundDefault] : []),
    ...summary.channels,
  ];
  const hit = all.find((c) => c.slug === param) || all.find((c) => c.groupId === param);
  return hit ? { groupId: hit.groupId, nome: hit.nome, slug: hit.slug } : null;
};

/** Valore da scrivere in `?canale=`: lo slug se c'e, altrimenti l'id. */
export const channelParamValue = (c: { groupId: string; slug?: string }): string => c.slug || c.groupId;

export interface RailChannelModel {
  groupId: string;
  nome: string;
  directHandling: boolean;
  hiddenFromAssociates: boolean;
  isActive: boolean;
  notTaken: number;
  inProgress: number;
  /** «N NON PRESI · N IN LAVORAZIONE» (L102-L105). */
  countsLabel: string;
  /** Rosso se ci sono non presi (L103, `npFg`). */
  notTakenHot: boolean;
  /** «Più vecchio non preso: X» (L106): solo se il canale ha non presi e qualcosa fuori SLA. */
  alert: string;
  /** Primi 4 componenti (L98-L100, L903). */
  members: DispatchChannelSummary['members'];
  extraMembers: number;
  /**
   * Aria-label del bottone della card: sostituisce il contenuto, quindi porta
   * nome, conteggi, badge («lavorazione diretta», «nascosto agli associati»,
   * «disattivato») e l'avviso «Più vecchio non preso».
   */
  ariaLabel: string;
}

export const MAX_RAIL_MEMBERS = 4;

/**
 * Un canale disattivato resta in colonna (DISATTIVATO) solo finche ha ticket
 * attivi. Il server li esclude gia (`dispatchSummary`): qui e una difesa.
 */
export const railChannelVisible = (c: DispatchChannelSummary): boolean =>
  c.isActive !== false || c.notTaken + c.inProgress + (c.waiting || 0) > 0;

export const buildRailChannel = (c: DispatchChannelSummary, now: Date = new Date()): RailChannelModel => {
  const alert =
    c.notTaken > 0 && c.slaBreached > 0 && c.oldestNotTakenAt
      ? `Più vecchio non preso: ${relativeAge(c.oldestNotTakenAt, now)}`
      : '';
  return {
    groupId: c.groupId,
    nome: c.nome,
    directHandling: c.directHandling,
    hiddenFromAssociates: c.hiddenFromAssociates,
    isActive: c.isActive,
    notTaken: c.notTaken,
    inProgress: c.inProgress,
    countsLabel: `${c.notTaken} NON PRESI · ${c.inProgress} IN LAVORAZIONE`,
    notTakenHot: c.notTaken > 0,
    alert,
    members: c.members.slice(0, MAX_RAIL_MEMBERS),
    extraMembers: Math.max(0, (c.memberCount ?? c.members.length) - MAX_RAIL_MEMBERS),
    ariaLabel: [
      `${c.nome}: ${c.notTaken} non presi, ${c.inProgress} in lavorazione`,
      c.directHandling ? 'lavorazione diretta' : '',
      c.hiddenFromAssociates ? 'nascosto agli associati' : '',
      c.isActive ? '' : 'disattivato',
      alert,
    ]
      .filter(Boolean)
      .join('; '),
  };
};

// ───────────────────────── Trasferimento e drag & drop ─────────────────────────

export interface TransferRules {
  /** Permesso `supporttickets.transfer`. */
  canTransfer: boolean;
  /** Regola di riga del T31 (componente del canale del ticket); assente = si. */
  canRowTransfer?: (t: TicketListItem) => boolean;
  /** Canali di destinazione ammessi (quelli che il pannello rapido offre); assente = tutti i canali attivi della colonna. */
  allowedTargets?: ReadonlySet<string> | null;
}

/** Il ticket si puo trasferire dall'utente corrente (bottone, clic, trascinamento). */
export const ticketTransferable = (t: TicketListItem, rules: TransferRules): boolean => {
  if (!rules.canTransfer) return false;
  if (t.status === 'chiuso') return false;
  return rules.canRowTransfer ? rules.canRowTransfer(t) : true;
};

/** Il canale e una destinazione valida per QUESTO ticket (mai il canale attuale). */
export const dropTargetAllowed = (
  t: TicketListItem,
  target: { groupId: string; isActive?: boolean },
  rules: TransferRules
): boolean => {
  if (!ticketTransferable(t, rules)) return false;
  if (target.isActive === false) return false;
  if (t.groupId?._id && t.groupId._id === target.groupId) return false;
  if (rules.allowedTargets && !rules.allowedTargets.has(target.groupId)) return false;
  return true;
};

export const DRAG_TICKET_PREFIX = 'ticket:';
export const DROP_CHANNEL_PREFIX = 'channel:';

export const dragIdOf = (ticketId: string): string => `${DRAG_TICKET_PREFIX}${ticketId}`;
export const dropIdOf = (groupId: string): string => `${DROP_CHANNEL_PREFIX}${groupId}`;

/**
 * Esito del rilascio (Q3): la richiesta di APRIRE il pannello rapido con il
 * canale preselezionato, oppure null (rilascio fuori, stesso canale, non
 * ammesso). Il trasferimento vero lo fa il pannello, con motivo e conferma.
 */
export const resolveDrop = (
  ticket: TicketListItem | null | undefined,
  target: { groupId: string; isActive?: boolean } | null | undefined,
  rules: TransferRules
): { ticketId: string; groupId: string } | null => {
  if (!ticket || !target) return null;
  if (!dropTargetAllowed(ticket, target, rules)) return null;
  return { ticketId: ticket._id, groupId: target.groupId };
};

/**
 * Soglia del trascinamento (px): sotto e un clic, da qui in su e un drag. La
 * stessa per il sensore pointer di dnd-kit e per la guardia del clic della card.
 */
export const DRAG_ACTIVATION_PX = 6;

/** Azione di un comando della card («Apri ticket» / «Trasferisci»). */
export type DispatchCardAction = 'open' | 'transfer';

/**
 * Cosa fa un `click` arrivato sulla SUPERFICIE della card (non su un suo
 * bottone), dato dove era iniziata la pressione:
 * - pressione spostata di `DRAG_ACTIVATION_PX` o piu = era un trascinamento
 *   (rilasciato fuori da un canale o annullato): nessuna azione;
 * - pressione iniziata su un comando della card: vale quel comando. Succede
 *   quando fra pressione e rilascio la card si sposta sotto il puntatore
 *   (sollevamento in hover): il browser manda il `click` all'antenato comune,
 *   cioe la card, e prima scattava il pannello al posto di «Apri ticket»;
 * - altrimenti l'azione della card: pannello solo in «Da smistare»
 *   (`clickTransfers`), altrove apre il ticket.
 * Coordinate assenti (eventi sintetici) = nessuno spostamento.
 */
export const resolveCardClick = (
  press: { x?: number; y?: number; action: DispatchCardAction | null } | null,
  click: { x?: number; y?: number },
  card: { clickTransfers: boolean }
): DispatchCardAction | null => {
  if (press) {
    const { x: px, y: py } = press;
    const { x: cx, y: cy } = click;
    if ([px, py, cx, cy].every((v) => typeof v === 'number' && Number.isFinite(v))) {
      if (Math.hypot((cx as number) - (px as number), (cy as number) - (py as number)) >= DRAG_ACTIVATION_PX) return null;
    }
    if (press.action) return press.action;
  }
  return card.clickTransfers ? 'transfer' : 'open';
};

export const dragHint = (dragging: boolean): string =>
  dragging ? 'Rilascia su un canale per trasferire' : 'Trascina una card su un canale per trasferirla';

// ───────────────────────── Card ─────────────────────────

export interface DispatchCardModel {
  row: QueueRowModel;
  column: DispatchBoardColumn;
  /** SLA mostrato solo se «caldo» (in scadenza o sforato, L136, `slaHot` L818). */
  sla: S3SlaBadge | null;
  excerpt: string;
  /** «ATTESA 10 H» (N9) o ''. */
  waitLabel: string;
  showAvatars: boolean;
  /** Bottone «Trasferisci» visibile (colonna giusta E permesso). */
  showTransfer: boolean;
  /** Trascinabile e trasferibile (permesso + regola di riga T31). */
  transferable: boolean;
  /**
   * Il clic sulla card apre il pannello rapido: solo in «Da smistare» e se
   * trasferibile. Nelle altre colonne (o senza permesso) il clic apre il ticket.
   */
  clickTransfers: boolean;
}

export const waitLabelOf = (channelSince: string | undefined, now: Date): string => {
  if (!channelSince) return '';
  const age = relativeAge(channelSince, now);
  if (age === '—') return '';
  return `ATTESA ${age === 'ora' ? '< 1 MIN' : age.toUpperCase()}`;
};

export const buildDispatchCard = (
  t: BoardTicketListItem,
  col: DispatchColumnDef,
  ctx: { now?: Date; selfId?: string; defaultGroupIds?: ReadonlySet<string>; rules: TransferRules }
): DispatchCardModel => {
  const now = ctx.now || new Date();
  const info = classifySla(t, now);
  const hot = info && (info.level === 'warning' || info.level === 'breach');
  const transferable = ticketTransferable(t, ctx.rules);
  return {
    row: buildQueueRow(t, { now, selfId: ctx.selfId, defaultGroupIds: ctx.defaultGroupIds }),
    column: col.key,
    sla: hot ? slaBadge(info) : null,
    excerpt: col.excerpt ? (t.excerpt || '').trim() : '',
    waitLabel: col.wait ? waitLabelOf(t.channelSince, now) : '',
    showAvatars: col.avatars && (t.assignees || []).length > 0,
    showTransfer: col.transferButton && transferable,
    transferable,
    clickTransfers: col.key === 'smi' && transferable,
  };
};

// ───────────────────────── Interruttore Bacheca | Elenco (Q5) ─────────────────────────

export type DispatchLayout = 'board' | 'list';

export const dispatchLayoutKey = (userId?: string): string => `supportCenter.dispatchLayout:${userId || 'anon'}`;

export const parseDispatchLayout = (raw: string | null | undefined): DispatchLayout => (raw === 'list' ? 'list' : 'board');
